import convert from "heic-convert/browser";

const MAX_DIMENSION = 2048;
const JPEG_QUALITY = 0.8;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const isHeic = (file) =>
  file.name.toLowerCase().endsWith(".heic") ||
  file.type === "image/heic";

const decodeToBitmap = async (file) => {
  let blob = file;
  if (isHeic(file)) {
    const buffer = new Uint8Array(await file.arrayBuffer());
    const jpegBuffer = await convert({
      buffer,
      format: "JPEG",
      quality: JPEG_QUALITY,
    });
    blob = new Blob([jpegBuffer], { type: "image/jpeg" });
  }
  const bitmap = await createImageBitmap(blob);
  return bitmap;
};

const drawScaled = (bitmap, maxDimension) => {
  const scale = Math.min(
    1,
    maxDimension / Math.max(bitmap.width, bitmap.height)
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext("2d");
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas;
};

const canvasToBlob = (canvas, quality = JPEG_QUALITY) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("canvas encoding failed"))),
      "image/jpeg",
      quality
    );
  });

const encodeUnderSize = async (bitmap, maxDimension) => {
  let canvas = drawScaled(bitmap, maxDimension);
  let blob = await canvasToBlob(canvas);
  let quality = JPEG_QUALITY;
  while (blob.size > MAX_FILE_BYTES && quality > 0.5) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, quality);
  }
  while (blob.size > MAX_FILE_BYTES && canvas.width > 640) {
    canvas = drawScaled(bitmap, Math.max(640, canvas.width * 0.8));
    blob = await canvasToBlob(canvas, quality);
  }
  return blob;
};

// Always returns a JPEG file for client compatibility; optionally caps
// the long edge at maxDimension for smaller email/text friendly files.
// Files larger than MAX_FILE_BYTES are progressively re-encoded smaller
// until they fit, since SharePoint uploads should stay under that size.
const normalizeImage = async (file, fileName, { reduce = true } = {}) => {
  const bitmap = await decodeToBitmap(file);
  const blob = await encodeUnderSize(
    bitmap,
    reduce ? MAX_DIMENSION : bitmap.width
  );
  bitmap.close?.();
  const baseName = fileName.replace(/\.[^.]*$/, "");
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
};

// Lightweight preview data URL without re-encoding the full image
const previewImage = async (file) => {
  const bitmap = await decodeToBitmap(file);
  const canvas = drawScaled(bitmap, 320);
  const url = canvas.toDataURL("image/jpeg", 0.7);
  bitmap.close?.();
  return url;
};

export { normalizeImage, previewImage, MAX_FILE_BYTES };
